import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCreateNumberingSeries } from '~/api/numberingSeries';
import { useUser } from '~/api/user';
import { Button, Dialog } from '~/components';
import { Field, Form, Input, Select } from '~/components/Form';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { CreateNumberingSeriesFormType, CreateNumberingSeriesModalProps as Props } from './types';

import { createFieldsStyle, createFormStyle } from './NumberingSeries.css';
import { getDefaultNumberingSeriesOptions, getNumberingSeriesEnabledFor } from './utils';

const typeItems = (['invoices', 'credit_notes', 'both'] as const).map((type) => ({ label: type, value: type }));

export const CreateNumberingSeriesModal = ({ onClose, onCreate }: Props) => {
  const { data: user } = useUser();
  const mutation = useCreateNumberingSeries();

  const { control, handleSubmit } = useForm<CreateNumberingSeriesFormType>({
    defaultValues: {
      name: '',
      type: 'invoices',
    },
  });

  const { t } = useTranslation(['common', 'settings', 'validation']);

  const onSubmit = handleSubmit((data) => {
    const { type, ...rest } = data;

    mutation.mutate(
      {
        ...rest,
        ...getNumberingSeriesEnabledFor(type),
        ...getDefaultNumberingSeriesOptions(user.numberingSeries),
      },
      {
        onSuccess: (numberingSeries) => {
          toast.success({ description: t('settings:alerts.numberingSeries.successCreated') });
          onCreate(numberingSeries.id);
          onClose();
        },
        onError: () => toast.danger({ description: t('common:error') }),
      },
    );
  });

  const items = typeItems.map(({ value }) => ({ value, label: t(`settings:fields.numberingSeries.type.options.${value}`) }));

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body>
        <Dialog.Header title={t('settings:general.numberingSeries.add')} />

        <Form className={createFormStyle} onSubmit={onSubmit}>
          <div className={createFieldsStyle}>
            <Controller
              control={control}
              name="name"
              render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid} name={name}>
                  <Field.Label infotip={t('settings:fields.numberingSeries.name.tooltip')}>
                    {t('settings:fields.numberingSeries.name.label')}
                  </Field.Label>

                  <Input
                    onBlur={onBlur}
                    onValueChange={onChange}
                    placeholder={t('settings:fields.numberingSeries.name.placeholder')}
                    ref={ref}
                    value={value}
                  />

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{
                required: { value: true, message: t('validation:required') },
                validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                maxLength: {
                  value: 255,
                  message: t('validation:maxLength', { attribute: t('settings:fields.numberingSeries.name.label'), max: 255 }),
                },
              }}
            />

            <Controller
              control={control}
              name="type"
              render={({ field: { name, onChange, value }, fieldState: { error, invalid } }) => (
                <Field.Root invalid={invalid} name={name}>
                  <Field.Label infotip={t('settings:fields.numberingSeries.type.tooltip')}>
                    {t('settings:fields.numberingSeries.type.label')}
                  </Field.Label>

                  <Select.Root items={items} onValueChange={onChange} value={value}>
                    <Select.Button />

                    <Select.List>
                      {items.map((item) => (
                        <Select.Item key={item.value} value={item.value}>
                          {item.label}
                        </Select.Item>
                      ))}
                    </Select.List>
                  </Select.Root>

                  <Field.Error match={!!error}>{error?.message}</Field.Error>
                </Field.Root>
              )}
              rules={{ required: { value: true, message: t('validation:required') } }}
            />
          </div>

          <Dialog.Actions>
            <Button icon="Add" loading={mutation.isPending} type="submit">
              {t('settings:general.numberingSeries.add')}
            </Button>
          </Dialog.Actions>
        </Form>
      </Dialog.Body>
    </Dialog.Root>
  );
};
